import { Constants } from '../../../../../shared/constants/create-survey.constants';
import { BuyerSurveyResponse } from '../../../../../shared/interfaces/survey.interface';

interface Decision {
  decide(): boolean;
}

export class McSameSettingsDecisionMaker {
  constructor(private readonly _surveys: BuyerSurveyResponse[]) {}

  public decide(key: string): boolean {
    switch (key) {
      case 'buyer_url':
        return new McBuyerUrlSameSettingDecisionMaker(this._surveys).decide();
      case 'billing_id':
        return new McBillingSettingsDecisionMaker(this._surveys).decide();
      case 'dq_settings':
        return new McDqSettingsSameSettingDecisionMaker(this._surveys).decide();
      case 'launch_settings':
        return new McLaunchSettingsSameSettingDecisionMaker(
          this._surveys
        ).decide();
      case 'quota_settings':
        return new McQuotaSettingsSameSettingDecisionMaker(
          this._surveys
        ).decide();
      case 'filter_audience':
        return new McFilterAudienceSameSettingDecisionMaker(
          this._surveys
        ).decide();
      case 'email_features':
        return new McEmailFeaturesSameSettingDecisionMaker(
          this._surveys
        ).decide();
      case 'unique_links':
        return new McSameFileEntryLinksDecesionMaker(this._surveys).decide();
      default:
        return Constants.MULTICOUNTRY.SINGLE_VIEW_SURVEY
          .SAME_ACROSS_LOCALES_SETTING_DEFAULT;
    }
  }
}

export class McBuyerUrlSameSettingDecisionMaker implements Decision {
  constructor(private readonly _surveys: BuyerSurveyResponse[]) {}

  public decide(): boolean {
    return [
      () => this._hasUniqueLiveUrl(),
      () => this._hasUniqueTestUrl(),
    ].every((check) => check());
  }

  private _hasUniqueLiveUrl(): boolean {
    return (
      [...new Set(this._surveys.map((s) => s.live_url || ''))].length === 1
    );
  }

  private _hasUniqueTestUrl(): boolean {
    return (
      [...new Set(this._surveys.map((s) => s.test_url || ''))].length === 1
    );
  }
}

export class McBillingSettingsDecisionMaker implements Decision {
  constructor(private readonly _surveys: BuyerSurveyResponse[]) {}

  public decide(): boolean {
    return [() => this._hasSameBillingId()].every((check) => check());
  }

  private _hasSameBillingId(): boolean {
    return (
      [...new Set(this._surveys.map((s) => s.billing_id || ''))].length === 1
    );
  }
}

export class McDqSettingsSameSettingDecisionMaker implements Decision {
  constructor(private readonly _surveys: BuyerSurveyResponse[]) {}

  public decide(): boolean {
    return [
      () => this._uniqueDqEnabled(),
      () => this._uniquePureScoreFilter(),
      () => this._uniqueFpDeviceCheck(),
      () => this._uniqueFpFraudCheck(),
    ].every((check) => check());
  }

  private _uniqueDqEnabled(): boolean {
    return [...new Set(this._surveys.map((s) => s.is_dq_enable))].length === 1;
  }

  private _uniquePureScoreFilter(): boolean {
    return (
      [
        ...new Set(
          this._surveys.map((s) => s.dq_settings?.purescore_filter_enabled)
        ),
      ].length === 1
    );
  }

  private _uniqueFpDeviceCheck(): boolean {
    return (
      [...new Set(this._surveys.map((s) => s.dq_settings?.fp_device_check))]
        .length === 1
    );
  }

  private _uniqueFpFraudCheck(): boolean {
    return (
      [...new Set(this._surveys.map((s) => s.dq_settings?.fp_fraud_check))]
        .length === 1
    );
  }
}

export class McLaunchSettingsSameSettingDecisionMaker implements Decision {
  constructor(private readonly _surveys: BuyerSurveyResponse[]) {}

  public decide(): boolean {
    return [
      () => this._hasUniqueBasicToken(),
      () => this._hasUniqueScheduleLaunch(),
      () => this._hasUniqueSoftLaunch(),
    ].every((check) => check());
  }

  private _hasUniqueBasicToken(): boolean {
    return (
      [...new Set(this._surveys.map((s) => JSON.stringify(s.basic_token)))]
        .length === 1
    );
  }

  private _hasUniqueScheduleLaunch(): boolean {
    return (
      [...new Set(this._surveys.map((s) => JSON.stringify(s.schedule_launch)))]
        .length === 1
    );
  }

  private _hasUniqueSoftLaunch(): boolean {
    return (
      [...new Set(this._surveys.map((s) => JSON.stringify(s.soft_launch)))]
        .length === 1
    );
  }
}

export class McQuotaSettingsSameSettingDecisionMaker implements Decision {
  constructor(private readonly _surveys: BuyerSurveyResponse[]) {}

  public decide(): boolean {
    return [
      () => this._hasUniqueQuotaThrottling(),
      () => this._hasUniqueFeot(),
    ].every((check) => check());
  }

  private _hasUniqueQuotaThrottling(): boolean {
    return (
      [...new Set(this._surveys.map((s) => s.quota_throttling))].length === 1
    );
  }

  private _hasUniqueFeot(): boolean {
    return [
      () => this._hasUniqueFeotEnabled(),
      () => this._hasUniqueFeotSettings(),
    ].every((check) => check());
  }

  private _hasUniqueFeotEnabled(): boolean {
    return (
      [...new Set(this._surveys.map((s) => s.is_feot_enabled))].length === 1
    );
  }

  private _hasUniqueFeotSettings(): boolean {
    return (
      [...new Set(this._surveys.map((s) => JSON.stringify(s.feot)))].length ===
      1
    );
  }
}

export class McFilterAudienceSameSettingDecisionMaker implements Decision {
  constructor(private readonly _surveys: BuyerSurveyResponse[]) {}

  public decide(): boolean {
    return [
      () => this._hasUniqueExclusion(),
      () => this._hasUniqueSurveyGrouping(),
      () => this._hasUniqueExternalId(),
    ].every((check) => check());
  }

  private _hasUniqueExclusion(): boolean {
    return [...new Set(this._surveys.map((s) => s.incl_excl))].length === 1;
  }

  private _hasUniqueSurveyGrouping(): boolean {
    return (
      [
        ...new Set(
          this._surveys.map((s) => JSON.stringify(s.survey_grouping || []))
        ),
      ].length === 1
    );
  }

  private _hasUniqueExternalId(): boolean {
    return (
      [...new Set(this._surveys.map((s) => s.survey_external_id))].length === 1
    );
  }
}

export class McEmailFeaturesSameSettingDecisionMaker implements Decision {
  constructor(private readonly _surveys: BuyerSurveyResponse[]) {}

  public decide(): boolean {
    return [
      () => this._hasUniqueSubscriberEmails(),
      () => this._hasUniquePmEmials(),
      () => this._hasUniqueBuyerMessage(),
      () => this._hasUniqueBuyerProjectManager(),
    ].every((check) => check());
  }

  private _hasUniqueSubscriberEmails(): boolean {
    return (
      [
        ...new Set(
          this._surveys.map((s) => JSON.stringify(s.subscriber_emails || []))
        ),
      ].length === 1
    );
  }

  private _hasUniquePmEmials(): boolean {
    return (
      [...new Set(this._surveys.map((s) => JSON.stringify(s.pm_emails || [])))]
        .length === 1
    );
  }

  private _hasUniqueBuyerMessage(): boolean {
    return (
      [...new Set(this._surveys.map((s) => s.buyer_message || ''))].length === 1
    );
  }

  private _hasUniqueBuyerProjectManager(): boolean {
    return (
      [...new Map(this._surveys.map((s) => [s.bpm?.id, s.bpm])).values()]
        .length === 1
    );
  }
}

export class McSameFileEntryLinksDecesionMaker implements Decision {
  constructor(private readonly _surveys: BuyerSurveyResponse[]) {}
  public decide(): boolean {
    return [
      () => this._isEnableUniqueLinksInAllChildSurveys(),
      () => this._isSameuniqueItemsLength(),
      () => this._isSameUniqueItemsInAllChildSurvey(),
    ].every((check) => check());
  }

  private _isEnableUniqueLinksInAllChildSurveys() {
    return !this._surveys.some((survey) => !survey.unique_links?.enable);
  }

  private _isSameuniqueItemsLength() {
    return this._surveys.every(
      (survey) =>
        survey.unique_links.items.length ===
        this._surveys[0].unique_links.items.length
    );
  }

  private _isSameUniqueItemsInAllChildSurvey() {
    const { items: referenceItems } = this._surveys[0].unique_links;
    return this._surveys.every((item) => {
      const currentItems = item.unique_links.items;
      return (
        currentItems.length === referenceItems.length &&
        currentItems.every(
          (current, index) => current.link === referenceItems[index].link
        )
      );
    });
  }
}
